import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { QueryOptions, useMutation, useQuery } from '@globalconnect/query';
import { FeedbackAlertService } from '../../../../shared/feedback-alert/service/feedback-alert.service';
import { ImagePreviewV2InitService } from '../../../../shared/image-preview-v2/service/init/image-preview-v2-init.service';
import { FileMetaData } from '../../../installation-report-content-v2/self-inspection-v2/self-inspection-v2-entities';
import {
  DEVIATION_COMMENT_ADDED,
  DEVIATION_IMAGE_UPLOADED,
} from '../../../posthug-analytics/analytics-event-types';
import { AnalyticsService } from '../../../posthug-analytics/analytics.service';
import { LoadingStatesService } from '../../report-inputs/service/loading-states.service';
import { RolloutProgressReportEntities } from '../entities';
import { rpReportAnswerKey } from '../entities/query-keys';
import { RolloutProgressReportFilesService } from '../files/rollout-progress-report-files.service';
import { RolloutProgressReportGroupsService } from '../groups/rollout-progress-report-groups.service';
import { InspectionReportInitService } from '../initial-values/inspection-report-init.service';
import { RolloutProgressReportsService } from '../reports/rollout-progress-reports.service';
import { RolloutProgressReportAnswersApiService } from './api/rollout-progress-report-answers-api.service';

@Injectable({
  providedIn: 'root',
})
export class RolloutProgressReportAnswersService {
  private readonly apiService = inject(RolloutProgressReportAnswersApiService);
  private readonly reportGroupService = inject(
    RolloutProgressReportGroupsService,
  );
  private readonly reportService = inject(RolloutProgressReportsService);
  private readonly feedbackAlertService = inject(FeedbackAlertService);
  private readonly loadingStatesService = inject(LoadingStatesService);
  private readonly imagePreviewInitService = inject(ImagePreviewV2InitService);
  private readonly fileService = inject(RolloutProgressReportFilesService);
  private readonly initService = inject(InspectionReportInitService);
  private readonly analyticsService = inject(AnalyticsService);

  isSiteAndHubReport = this.initService.isSiteAndHubReport;

  getAnswer$(
    reportId: string,
    definitionId: string,
    reportGroup: RolloutProgressReportEntities.v3.Groups,
    options?: Partial<
      QueryOptions<
        RolloutProgressReportEntities.v3.AnswerResponse,
        HttpErrorResponse,
        never,
        RolloutProgressReportEntities.v3.AnswerResponse
      >
    >,
  ) {
    return useQuery(
      [rpReportAnswerKey, definitionId],
      () => this.apiService.getAnswer(reportId, definitionId),
      {
        onSuccess: (answerResponse) => {
          this.reportGroupService.getReportGroupByName$(
            reportId,
            reportGroup,
            answerResponse.selfInspectionDefinitionId,
            answerResponse,
          );
        },
        onError: (err) => console.error(err),
        cacheTime: 0,
        staleTime: 0,
        ...options,
      },
    ).getState$();
  }

  createAnswer$ = (
    reportId: string,
    definitionId: string,
    request: RolloutProgressReportEntities.v3.AnswerRequestType,
    reportGroup: RolloutProgressReportEntities.v3.Groups,
  ) =>
    useMutation(
      () => this.apiService.createAnswer(reportId, definitionId, request),
      {
        onSuccess: (answerResponse) => {
          if (this.isDevationGroup(reportGroup)) {
            this.reportService.getReportWithInitValues();
            this.analyticsService.logEvent(DEVIATION_COMMENT_ADDED);
          }

          this.isSiteAndHubReport()
            ? this.reportGroupService.getReportGroupByName$(
                reportId,
                reportGroup,
                answerResponse.answer.selfInspectionDefinitionId,
                answerResponse.answer,
              )
            : this.reportGroupService.addAnswerToStoredInspectionReportGroups(
                reportId,
                answerResponse,
                reportGroup,
              );
          this.feedbackAlertService.showAlert(
            'success',
            'feedbackAlert.save.success',
          );
        },
        onError: () => {
          this.loadingStatesService.setIsLoadingValue(definitionId, false);
          this.feedbackAlertService.showAlert(
            'critical',
            'feedbackAlert.save.error',
          );
        },
      },
    );

  deleteAnswer$ = (
    reportId: string,
    definitionId: string,
    reportGroup: RolloutProgressReportEntities.v3.Groups,
  ) =>
    useMutation(() => this.apiService.deleteAnswer(reportId, definitionId), {
      onSuccess: () => {
        this.isDevationGroup(reportGroup) &&
          this.reportService.getReportWithInitValues();
        this.feedbackAlertService.showAlert(
          'success',
          'feedbackAlert.undoSelfInspection.success',
        );
        this.reportGroupService.getReportGroupByName$(
          reportId,
          reportGroup,
          definitionId,
          {} as RolloutProgressReportEntities.v3.AnswerResponse,
        );
      },
      onError: () => {
        this.loadingStatesService.setIsLoadingValue(definitionId, false);
        this.feedbackAlertService.showAlert(
          'critical',
          'feedbackAlert.undoSelfInspection.error',
        );
      },
    });

  createFile$ = (
    reportId: string,
    definitionId: string,
    fileFormData: FormData,
    reportGroup: RolloutProgressReportEntities.v3.Groups,
    isFileAndText: boolean,
    isInsideModal: boolean,
  ) =>
    useMutation(
      () => this.apiService.createFile(reportId, definitionId, fileFormData),
      {
        onSuccess: (fileAnswerResponse) => {
          if (this.isDevationGroup(reportGroup)) {
            this.reportService.getReportWithInitValues();
            this.analyticsService.logEvent(DEVIATION_IMAGE_UPLOADED);
          }
          if (isInsideModal) {
            this.imagePreviewInitService.addAttachmentsToImagePreview(
              (fileId) => this.fileService.getFile$(fileId),
              isFileAndText
                ? (fileAnswerResponse.answer.fileAndText
                    ?.fileMetas as FileMetaData[])
                : (fileAnswerResponse.answer.files as FileMetaData[]),
            );
          }
          this.isSiteAndHubReport()
            ? this.getAnswer$(reportId, definitionId, reportGroup)
            : this.reportGroupService.addAnswerToStoredInspectionReportGroups(
                reportId,
                fileAnswerResponse,
                reportGroup,
              );
          this.loadingStatesService.setIsLoadingValue(definitionId, false);
          this.feedbackAlertService.showAlert(
            'success',
            'feedbackAlert.upload.success',
          );
        },
        onError: (err) => {
          this.loadingStatesService.setIsLoadingValue(definitionId, false);
          this.feedbackAlertService.showAlert(
            'critical',
            err.status === 413
              ? 'feedbackAlert.upload.tooLarge'
              : 'feedbackAlert.upload.error',
          );
        },
      },
    );

  deleteFile$ = (
    reportId: string,
    definitionId: string,
    fileId: string,
    reportGroup: RolloutProgressReportEntities.v3.Groups,
  ) =>
    useMutation(
      () => this.apiService.deleteFile(reportId, definitionId, fileId),
      {
        onSuccess: () => {
          this.isDevationGroup(reportGroup) &&
            this.reportService.getReportWithInitValues();
          this.imagePreviewInitService.removeAttachmentFromImagePreview(fileId);
          this.getAnswer$(reportId, definitionId, reportGroup);
          this.loadingStatesService.setIsLoadingValue(definitionId, false);
          this.feedbackAlertService.showAlert(
            'success',
            'feedbackAlert.remove.success',
          );
        },
        onError: () => {
          this.loadingStatesService.setIsLoadingValue(definitionId, false);
          this.feedbackAlertService.showAlert(
            'critical',
            'feedbackAlert.remove.error',
          );
        },
      },
    );

  isDevationGroup(groupName: RolloutProgressReportEntities.v3.Groups): boolean {
    const deviationGroups = Object.values(
      RolloutProgressReportEntities.v3.DeviationGroups,
    ) as string[];
    return deviationGroups.includes(groupName);
  }
}
